import {
  STOREFRONT_RARITY_TIERS,
  type StorefrontRarityKey,
  type StorefrontTokenKey,
} from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { useActiveEnvironmentId } from "./environment";
import { useTRPC } from "./trpc";

/**
 * The read half, for consumers that only render the config and have no business
 * owning a mutation or a "saved" timer — the box editor's storefront preview.
 *
 * The same query key is already in flight on every tenant page (ViewStorefrontButton
 * in TenantLayout), so an extra caller costs nothing and follows the environment
 * switcher for free.
 */
export function useStorefrontConfigQuery() {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  // The tenant and environment ride the x-loot-tenant / x-loot-environment headers;
  // they're returned for links and labels, not for the request.
  const environmentId = useActiveEnvironmentId() ?? undefined;
  const queryOptions = trpc.storefront.get.queryOptions();
  return { tenantId, environmentId, queryOptions, query: useQuery(queryOptions) };
}

/**
 * Shared wiring for the storefront section pages: the config query, the patch
 * mutation, and the "saved" toast. Every section sends only its own fields —
 * storefront.update takes each one optional and writes a plain patch, so a
 * section can never clobber a sibling's column.
 *
 * `onSaved` is where a page drops its local draft. Drafts are read as
 * `draft ?? config.x`, so one left standing after a save would keep shadowing
 * the value the server just returned.
 */
export function useStorefrontConfig(options?: { onSaved?: () => void }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { tenantId, environmentId, queryOptions, query } = useStorefrontConfigQuery();

  const update = useMutation(
    trpc.storefront.update.mutationOptions({
      onSuccess: (row) => {
        // The mutation returns the updated row, so seeding the cache with it
        // keeps the form from flashing stale values between the draft reset
        // and the refetch landing.
        // `update` never touches the shipping destinations, the legal identity, the banner
        // theme, the social links, the box categories, the terminology, the template, the page
        // layouts, the forge settings or the sounds, and the row carries all ten unparsed, so the parsed
        // values already in the cache are the ones to keep.
        queryClient.setQueryData(queryOptions.queryKey, (old) =>
          old
            ? {
                ...old,
                config: {
                  ...row,
                  shippingDestinations: old.config.shippingDestinations,
                  legal: old.config.legal,
                  cookieBannerTheme: old.config.cookieBannerTheme,
                  socialLinks: old.config.socialLinks,
                  boxCategories: old.config.boxCategories,
                  terminology: old.config.terminology,
                  storefrontTemplate: old.config.storefrontTemplate,
                  pageTemplates: old.config.pageTemplates,
                  forgeSettings: old.config.forgeSettings,
                  sounds: old.config.sounds,
                },
              }
            : old,
        );
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        options?.onSaved?.();
        toast.success("Storefront saved.");
      },
    }),
  );

  return { tenantId, environmentId, query, update, queryOptions };
}

const RADIUS_RE = /^(\d{1,3}(?:\.\d+)?)(px|rem|em)?$/;
/** The stylesheet's own --radius (0.625rem), in px — what an unset field shows. */
export const DEFAULT_RADIUS_PX = 10;
export const MAX_RADIUS_PX = 64;

/** Stored length → px, or null when it's unset or a form the slider can't hold. */
export function radiusToPx(value: string): number | null {
  const match = RADIUS_RE.exec(value.trim());
  if (!match) return null;
  const size = Number(match[1]);
  return match[2] === "rem" || match[2] === "em" ? size * 16 : size;
}

/** Stored token overrides, minus the blanks that mean "inherit the default". */
export function setTokenOverrides(
  tokens: Partial<Record<StorefrontTokenKey, string>>,
): Partial<Record<StorefrontTokenKey, string>> {
  return Object.fromEntries(
    Object.entries(tokens).filter(([, value]) => (value ?? "").trim() !== ""),
  );
}

/**
 * Stored rarity overrides, minus the blanks that mean "inherit the rail palette".
 *
 * Built by walking the tier table rather than the stored map — unlike the token
 * keys, the tier list has shrunk, and a theme saved against the old one can
 * still carry a key the contract rejects. Echoing that key back would fail
 * validation on save and strand staff on a page they can't submit.
 */
export function setRarityOverrides(
  rarity: Partial<Record<StorefrontRarityKey, string>>,
): Partial<Record<StorefrontRarityKey, string>> {
  const overrides: Partial<Record<StorefrontRarityKey, string>> = {};
  for (const tier of STOREFRONT_RARITY_TIERS) {
    const value = (rarity[tier.key] ?? "").trim();
    if (value) overrides[tier.key] = value;
  }
  return overrides;
}

/** How many fields a tenant has actually set, for the Theme tab's "N of M" summary. */
export function countOverrides(values: Record<string, string | undefined> | null | undefined) {
  return Object.values(values ?? {}).filter((value) => (value ?? "").trim() !== "").length;
}
